import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import ModalContext from 'contexts/ModalContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import MasterModalContext from 'contexts/MasterModalContext'
import {
  get as getLocalStorage,

  set as setLocalStorage,
} from 'lib/storage/isomorphicLocalStorage'
import { matchPath } from 'react-router'
import { getHasLuxLoyaltyAccount, getIsLuxLoyaltyEnabled } from 'luxLoyalty/selectors/luxLoyaltyFeatureToggles'
import { LUX_LOYALTY } from 'luxLoyalty/constants/base'
import { clearLuxLoyaltyEnrolment, enrolLuxLoyaltyAccount, fetchLuxLoyaltyAccountEligibility } from 'actions/LuxLoyaltyActions'
import LuxLoyaltyJoinModal from 'luxLoyalty/components/LuxLoyaltyJoinModal'
import getLuxLoyaltyEligibleTier from 'luxLoyalty/selectors/getLuxLoyaltyEligibleTier'
import { capitalise } from 'lib/string/stringUtils'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'

const pathsBlacklist = [
  '/:region/checkout',
  '/:region/booking/confirmation',
  `/:region/${LUX_LOYALTY.LANDING_PAGE}`,
  '/:region/offer',
]

function useLuxLoyaltyEnrolExistingUserModal() {
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  const loggedIn = useAppSelector(selectLoggedIn)
  const luxLoyaltyEnabled = useAppSelector(getIsLuxLoyaltyEnabled)
  const modalState = useContext(MasterModalContext)
  const hasLuxLoyaltyAccount = useAppSelector(getHasLuxLoyaltyAccount)
  const luxLoyaltyAccount = useAppSelector(state => state.luxLoyalty.account)
  const luxLoyaltyEligibility = useAppSelector(state => state.luxLoyalty.eligibilityTier)
  const isDismissed = !!getLocalStorage(LUX_LOYALTY.DISMISS_JOIN_MODAL_STORAGE_KEY)
  const accountEligibleTier = useAppSelector(getLuxLoyaltyEligibleTier)

  const path = useAppSelector(state => state.router.location.pathname)

  const isModalPreventedPage = !!matchPath(path, {
    path: pathsBlacklist,
    exact: false,
  })

  const shouldFetchEligibility = loggedIn && luxLoyaltyEnabled && !isDismissed && !hasLuxLoyaltyAccount

  const shouldShowModal = loggedIn &&
  luxLoyaltyEnabled &&
  !isDismissed &&
  !modalState.open &&
  !hasLuxLoyaltyAccount &&
  !luxLoyaltyAccount.fetching &&
  !luxLoyaltyAccount.error &&
  !isModalPreventedPage &&
  !luxLoyaltyEligibility.fetching &&
  !luxLoyaltyEligibility.error &&
  !!luxLoyaltyEligibility.data

  useEffect(() => {
    if (shouldFetchEligibility) {
      dispatch(fetchLuxLoyaltyAccountEligibility())
    }
  }, [shouldFetchEligibility, dispatch])

  const formattedTier = capitalise(accountEligibleTier)

  const handleClearEnrolment = useCallback(() => {
    dispatch(clearLuxLoyaltyEnrolment())
    setLocalStorage(LUX_LOYALTY.DISMISS_JOIN_MODAL_STORAGE_KEY, 1)
  }, [dispatch])

  const dismissModal = useCallback((resolve: (result?: any) => void) => {
    setLocalStorage(LUX_LOYALTY.DISMISS_JOIN_MODAL_STORAGE_KEY, 1)
    resolve()
  }, [])

  const handleOnClickJoinLuxLoyalty = useCallback((resolve: (result?: any) => void) => {
    dismissModal(resolve)
    dispatch(enrolLuxLoyaltyAccount({ enrolment: 'existingUserRegistration' }))
  }, [dismissModal, dispatch])

  const primaryActionProps = useMemo<React.ComponentProps<typeof ModalFooter>['primaryActionProps']>(() => ({
    children: `Activate my FREE ${formattedTier} Status`,
    onClick: (_e, resolve) => handleOnClickJoinLuxLoyalty(resolve),
  }), [formattedTier, handleOnClickJoinLuxLoyalty])

  const secondaryActionProps = useMemo<React.ComponentProps<typeof ModalFooter>['secondaryActionProps']>(() => ({
    children: 'Maybe later',
    onClick: (_e, resolve) => dismissModal(resolve),
  }), [dismissModal])

  useEffect(() => {
    if (shouldShowModal) {
      showModal(<LuxLoyaltyJoinModal
        onAfterClose={handleClearEnrolment}
        primaryActionProps={primaryActionProps}
        secondaryActionProps={secondaryActionProps}
      />)
    }
  }, [handleClearEnrolment, primaryActionProps, secondaryActionProps, shouldShowModal, showModal])
}

export default useLuxLoyaltyEnrolExistingUserModal
